/**
 *
 * AbsenceMobile
 *
 */

import React, { memo, useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';

// styles
import './style.scss';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import reducer from './reducer';
import saga from './saga';

import {
  flushStateAbsenceMobile,
  loadAbsencesMobile,
  changeAbsenceMobile,
  deleteAbsenceMobile,
} from './actions';

import {
  selectAbsenceMobileLoading,
  selectAbsenceMobileError,
  selectAbsenceMobileDeleteSuccessful,
  selectAbsenceMobileData,
  selectAbsenceMobileList,
} from './selectors';

// antd component
import { Layout, List, Button, Tabs } from 'antd';

// assets
import { PlusOutlined, QuestionOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import CustomIcons from 'components/CustomIcons';

// mobile containers
//import AbsenceMobileDetail from 'mobile/containers/AbsenceMobileDetail';

// components
import SideListItem from 'components/SideListItem';
import Preloader from 'components/Preloader';

// mobile components
import InfoModal from 'mobile/components/InfoModal';
import NoAbsence from 'components/Absences/NoAbsence';
import CreateAbsence from 'mobile/components/CreateAbsence';
import HomeDonatChart from 'components/Charts/HomeDonatChart';

// global user
import {
  useGetLocalUser,
  useGetLocalActiveChild,
} from 'engine/Contexts/User.context';

import { STATUSES_LIST } from 'utils/helpers/helper.constant';

export function AbsenceMobile({
  // main data
  absenceList,
  absenceData,
  // states
  deleteSuccessful,
  error,
  loading,
  history,
  // actions
  loadAbsencesMobile,
  changeAbsenceMobile,
  flushState,
}) {
  useInjectReducer({ key: 'absenceMobile', reducer });
  useInjectSaga({ key: 'absenceMobile', saga });

  const localUser = useGetLocalUser()();
  const activeChild = useGetLocalActiveChild()();
  const { role } = localUser;

  const [createNew, setCreateNew] = useState(false);
  const [openInfo, setOpenInfo] = useState(false);

  const [absenceOnEdit, setAbsenceOnEdit] = useState(false);

  const [showFinished, setShowFinished] = useState(false);
  const [absences, setAbsences] = useState([]);
  const [finished, setFinished] = useState([]);
  const [dataNoProcessed, setDataNoProcessed] = useState([]);

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    // --> main code here

    if (role == 'parent' && activeChild?._id) {
      !absenceList && loadAbsencesMobile(role, activeChild?._id);
    } else if (role == 'teacher') {
      !absenceList && loadAbsencesMobile(role, false);
    }

    // Anything in here is fired on component unmount.
    return () => {
      // Clearing the state after unmounting a component
      flushState();
      setAbsenceOnEdit(false);
      setShowFinished(false);
      setCreateNew(false);
    };
  }, []);

  useEffect(() => {
    if (role == 'parent' && activeChild?._id) {
      loadAbsencesMobile(role, activeChild?._id);
    }
  }, [activeChild?._id]);

  useEffect(() => {
    if (absenceList) {
      const finishedList = absenceList?.data.filter(abs => {
        if (
          abs?.status == 'accepted' ||
          abs?.status == 'sickness-finish' ||
          abs?.status == 'refused'
        ) {
          return abs;
        }
      });

      setFinished(finishedList);
      const otherList = absenceList?.data.filter(
        abs => !finishedList.includes(abs),
      );
      const processedList = absenceList?.data.filter(
        abs => finishedList.includes(abs),
      );
      setDataNoProcessed(otherList);
      setAbsences(processedList);
    }
  }, [loading]);

  const changeAbsenceType = () => {
    setShowFinished(!showFinished);
  };

  const openHandler = value => {
    setCreateNew(value);
    if (!value) {
      setAbsenceOnEdit(false);
    }
  };

  const renderListImem = useCallback((item, index) => { 
    return (
      <SideListItem
        key={item._id}
        type="absence"
        onClickAction={() => {
          setAbsenceOnEdit(item);
          setCreateNew(true);
        }}
        element={item}
      />
    )
  }, []);

  let absListOptions = {
    header: "123",
    itemLayout: "vertical",
    size: "large",
    loading: ({
      spinning: loading,
      indicator: <Preloader />,
    }),
    renderItem: renderListImem,
    locale: ({
      emptyText: (
        <NoAbsence
          title="Aucune absence à traiter"
          content="Vous avez traité toutes les absences"
          newAction={() => setCreateNew(true)}
          filterAction={() => setShowFinished(true)}
        />
      ),
    })
  }

  const getAbsenceStatusIcon = useCallback(({key, label}) => {
    switch (key) {
      case 'pending':
        return (
          <div className="status-item">
            <span className="status-tag status-tag--pending">
              <CustomIcons type="loop" />
            </span>
            <span className="status-label">{label}</span>
          </div>
        );
      case 'sick':
        return (
          <div className="status-item">
            <span className="status-tag status-tag--sick">
              <CustomIcons type="loop" />
            </span>
            <span className="status-label">{label}</span>
          </div>
        );
      case 'justification-missing':
        return (
          <div className="status-item">
            <span className="status-tag status-tag--justification-missing">
              <CustomIcons type="document" />
            </span>
            <span className="status-label">{label}</span>
          </div>
        );
      case 'sickness-finish':
        return (
          <div className="status-item">
            <span className="status-tag status-tag--sickness-finish">
              <CheckOutlined />
            </span>
            <span className="status-label">{label}</span>
          </div>
        );
      case 'accepted':
        return (
          <div className="status-item">
            <span className="status-tag status-tag--accepted">
              <CheckOutlined />
            </span>
            <span className="status-label">{label}</span>
          </div>
        );
      case 'refused':
        return (
          <div className="status-item">
            <span className="status-tag status-tag--refused">
              <CloseOutlined />
            </span>
            <span className="status-label">{label}</span>
          </div>
        );
    }
  }, []);

  const AbsTabHeader = useCallback(({
    key
  }) => {
    const collapsed = false;
    return (
      <div className="absence-header">
        <Button
          key={`add-new-abs-${key}`}
          className={`absence-new-btn ${collapsed ? 'collapsed-btn' : ''
            }`}
          onClick={() => openHandler(true)}
          disabled={!!!activeChild?._id && localUser?.role == 'parent'}
        >
          <PlusOutlined />
          {!collapsed ? (
            <span className="text">Nouvelle absence</span>
          ) : (
            ''
          )}
        </Button>
        <Button
          key={`abs-info-abs-${key}`}
          className="absence-info-btn"
          onClick={() => setOpenInfo(true)}
        >
          <QuestionOutlined />
        </Button>
      </div>
    );
  }, [activeChild?._id, localUser?.role]);

  const processedAbsTab = useMemo(() => {
    return (
      <>
        {AbsTabHeader({
          key: "processed-abs",
        })}
        <List
          {...absListOptions}
          header={
            dataNoProcessed?.length > 0 &&
            'Absences en cours de traitement'
          }
          className="absence-list processed"
          dataSource={dataNoProcessed}
        />
      </>
    )
  }, [absListOptions, dataNoProcessed, openHandler]);

  const finishedAbsTab = useMemo(() => {
    return (
      <>
        {AbsTabHeader({
          key: "finished-abs",
        })}
        <List
          {...absListOptions}
          header={absences?.length > 0 && 'Absences passées'}
          className="absence-list finished"
          dataSource={absences}
        />
      </>
    )
  }, [absListOptions, absences, openHandler]);

  const absChart = useMemo(() => <HomeDonatChart />, []);


  // render function
  return (
    <Layout className="main-full-content">
      <Helmet>
        <title>Absence</title>
        <meta name="description" content="Description of Absence" />
      </Helmet>
      <Layout.Content className="main-content-container absence-page">
        <CreateAbsence
          key={`CreateAbsence-add-new`}
          absence={absenceOnEdit}
          isOpen={createNew}
          setOpen={openHandler}
          change={changeAbsenceMobile}
          hideButton={true}
        />
        <InfoModal
          key={`InfoModal-modal-new`}
          isOpen={openInfo}
          setOpen={setOpenInfo}
          hideButton={true}
          title="Informations sur les statuts"
        >
          <div className="statuses-list-info">
            {STATUSES_LIST?.map(getAbsenceStatusIcon)}
          </div>
        </InfoModal>
        <Tabs
          defaultActiveKey="processed"
          className="absence-tabs"
          centered
          items={[
            {
              label: 'En traitement',
              key: 'processed',
              children: processedAbsTab,
            },
            {
              label: 'Traitees',
              key: 'finished',
              children: finishedAbsTab,
            },
            {
              label: 'Statistiques',
              key: 'abschart',
              children: absChart,
            },
          ]}
        />
      </Layout.Content>
    </Layout>
  );
}

AbsenceMobile.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  deleteSuccessful: PropTypes.bool,
  loadAbsencesMobile: PropTypes.func.isRequired,
  changeAbsenceMobile: PropTypes.func,
  deleteAbsenceMobile: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
  loading: selectAbsenceMobileLoading(),
  error: selectAbsenceMobileError(),
  deleteSuccessful: selectAbsenceMobileDeleteSuccessful(),
  absenceList: selectAbsenceMobileList(),
  absenceData: selectAbsenceMobileData(),
});

function mapDispatchToProps(dispatch) {
  return {
    flushState: () => dispatch(flushStateAbsenceMobile()),
    loadAbsencesMobile: (role, child) =>
      dispatch(loadAbsencesMobile(role, child)),
    changeAbsenceMobile: data => dispatch(changeAbsenceMobile(data)),
    deleteAbsenceMobile: id => dispatch(deleteAbsenceMobile(id)),
    dispatch,
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(AbsenceMobile);
